import { vec2, vec3 } from "gl-matrix";
import { PathNode } from "./PathNode";
import { GateResolver, Graph, PathFinder } from "@spi/Common/PathFinding";

export class NavigationMeshPathFinder extends PathFinder<PathNode> implements Graph<PathNode> {

    constructor(public gateResolver: GateResolver) {
        super();
        this.setGraph(this);

        this.setHeuristic((node: PathNode) => {
            return vec3.dist(node.getPosition(), this.targetPosition)
        });
    }

    public getCost(a: PathNode, b: PathNode): number {
        return 1;
    }

    public getNeighbours(element: PathNode): PathNode[] {
        return element.neighbours.filter(node => {
            return node.reference == '' || node.reference == null || this.gateResolver.testGate(node.reference)
        });
    }

    public optimizeMovementPath(currentNode: PathNode, path: ResultPathLike<PathNode>): PathSegment[] {
        if (path.path.length == 1 && path.path[0] == currentNode) {
            return [{
                node: currentNode,
                position: path.target
            }];
        }

        let walkPath = path.path.slice();
        walkPath.unshift(currentNode);

        let result: PathSegment[] = [];

        let pathDetails = this.createLinkedNodes(walkPath);

        let optimizer = new PathOptimizer();

        let start = optimizer.getSectionNode(pathDetails.head, this.originPosition);
        let goal = optimizer.getSectionNode(pathDetails.tail, this.targetPosition);

        let optimizedPath = optimizer.findPath(
            start,
            goal,
            this.originPosition,
            this.targetPosition
        );

        if (optimizedPath.solved) {

            let map = new Map<PathNode, vec3 | number[]>();

            for (let step of optimizedPath.path) {
                map.set(step.linkedNode.node, step.location);
            }

            map.forEach((location, node) => {
                result.push({
                    position: location,
                    node: node
                });
            })
        } else {
            console.warn('No solution found');
        }

        return result;
    }

    private createLinkedNodes(path: PathNode[]) {
        let tail: LinkedPathNode = null;
        let link = () => {
            if (path.length > 1) {
                let currentNode = path.shift();
                return new LinkedPathNode(currentNode, link());
            } else {
                tail = new LinkedPathNode(path.shift(), null);
                return tail
            }
        };
        let head = link();
        return {
            head: head,
            tail: tail
        };
    }

}

type ResultPathLike<T> = {
    path: T[],
    target: vec3 | number[],
    origin: vec3 | number[]
}

export type PathSegment = {
    position: vec3 | number[],
    node: PathNode
}

class LinkedPathNode {
    constructor(
        public node: PathNode,
        public next: LinkedPathNode) {

    }
}

class PathSectionNode {
    constructor(
        public linkedNode: LinkedPathNode,
        public location: vec3 | number[]) {
    }

    getNeighbours(optimizer: PathOptimizer): PathSectionNode[] {
        let offsets = [
            [1, 0],
            [0, 1],
            [-1, 0],
            [0, -1]
        ];

        let neighbours: PathSectionNode[] = [];

        for (let offset of offsets) {
            let location = [this.location[0] + offset[0], this.location[1] + offset[1], 0];
            location[2] = this.linkedNode.node.getHeight(location);

            if (this.contains(this.linkedNode.node, location)) {
                neighbours.push(optimizer.getSectionNode(this.linkedNode, location))
            } else if (this.linkedNode.next && this.contains(this.linkedNode.next.node, location)) {
                neighbours.push(optimizer.getSectionNode(this.linkedNode.next, location))
            }
        }

        if (neighbours.length == 0) {
            console.log('no neighbours found?');
        }

        return neighbours;
    }

    private contains(node: PathNode, location: vec2 | number[]): boolean {
        return location[0] >= node.min[0] && location[0] <= node.max[0]
            && location[1] >= node.min[1] && location[1] <= node.max[1];
    }
}

class PathOptimizer extends PathFinder<PathSectionNode> implements Graph<PathSectionNode> {

    private map: Map<PathNode, Map<string, PathSectionNode>>;

    constructor() {
        super();
        this.setGraph(this);

        this.map = new Map<PathNode, Map<string, PathSectionNode>>();

        this.setHeuristic((node: PathSectionNode) => {
            return vec3.dist(node.location, this.targetPosition)
        });
    }

    public getSectionNode(linkedNode: LinkedPathNode, location: vec3 | number[]): PathSectionNode {

        if (!this.map.has(linkedNode.node)) {
            this.map.set(linkedNode.node, new Map<string, PathSectionNode>());
        }
        let map = this.map.get(linkedNode.node);

        let coords = [
            Math.round(location[0]),
            Math.round(location[1]),
            Math.round(location[2])
        ];

        let key = `${coords[0]}-${coords[1]}`; // do not include z in map, path nodes are constructed from 2d information only
        if (map.has(key)) {
            return map.get(key);
        } else {
            let section = new PathSectionNode(linkedNode, coords);
            map.set(key, section);
            return section;
        }
    }

    getCost(a: PathSectionNode, b: PathSectionNode): number {
        return 1;
    }

    getNeighbours(element: PathSectionNode): PathSectionNode[] {
        return element.getNeighbours(this);
    }
}